import { useState } from "react";
import { api } from "../../lib/api";
import { t, componentTypeLabel } from "../../lib/i18n";
import { toast } from "sonner";
import { Field, Modal, MiniStat, StatSlider, COMPONENT_TYPES } from "./_shared";

export default function ComponentsAdmin({ components, refresh }) {
  const [form, setForm] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const empty = {
    name: "", type: "blade",
    attack: null, defense: null, stamina: null, dash: null, burst_resistance: null,
    weight: null, series: null, spin_direction: null,
    has_integrated_ratchet: false,
    is_cx_infinity: false,
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name, type: form.type,
        attack: form.attack, defense: form.defense, stamina: form.stamina,
        dash: form.dash, burst_resistance: form.burst_resistance,
        weight: form.weight, series: form.series,
        spin_direction: form.spin_direction,
        has_integrated_ratchet: !!form.has_integrated_ratchet,
        is_cx_infinity: !!form.is_cx_infinity,
      };
      if (form.id) await api.put(`/admin/components/${form.id}`, payload);
      else await api.post(`/admin/components`, payload);
      toast.success(t.saved);
      setForm(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };
  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/admin/components/${id}`);
    toast.success(t.deleted);
    refresh();
  };

  const bulkImport = async () => {
    const rows = bulkText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const idx = line.indexOf(",");
        if (idx === -1) return null;
        const type = line.slice(0, idx).trim().toLowerCase();
        const name = line.slice(idx + 1).trim();
        if (!type || !name) return null;
        return { type, name };
      })
      .filter(Boolean);
    if (rows.length === 0) {
      toast.error(t.failed);
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/components/bulk", rows);
      toast.success(`${t.bulkAdded}${r.data.created.length}${t.bulkSkipped}${r.data.skipped.length}`);
      setBulkText("");
      setBulkOpen(false);
      refresh();
    } catch (e) {
      toast.error(t.failed);
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = filterType === "all" ? components : components.filter((c) => c.type === filterType);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setForm(empty)} data-testid="new-component-btn"
            className="px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">
            {t.newComponent}
          </button>
          <button onClick={() => setBulkOpen(true)} data-testid="bulk-component-btn"
            className="px-4 py-2.5 border border-[#E2FF00]/40 text-[#E2FF00] hover:bg-[#E2FF00]/10 font-bold uppercase tracking-[0.25em] text-xs">
            ＋＋ {t.bulkAddBtn}
          </button>
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setFilterType("all")} data-testid="comp-filter-all"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border ${
              filterType === "all" ? "border-[#FF0055] text-[#FF0055] bg-[#FF0055]/10" : "border-white/15 text-white/60"
            }`}>
            {t.filterAll}
          </button>
          {COMPONENT_TYPES.map((ct) => (
            <button key={ct} onClick={() => setFilterType(ct)}
              data-testid={`comp-filter-${ct}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border ${
                filterType === ct ? "border-[#FF0055] text-[#FF0055] bg-[#FF0055]/10" : "border-white/15 text-white/60"
              }`}>
              {componentTypeLabel(ct)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`comp-row-${c.id}`} className="border border-white/10 bg-[#0C0C14] p-4 flex flex-col gap-3">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">{componentTypeLabel(c.type)}</div>
                <div className="font-display font-bold uppercase italic text-lg mt-1 truncate">{c.name}</div>
              </div>
              <div className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.25em] font-bold flex-shrink-0">
                <button onClick={() => setForm({
                  ...c,
                  attack: c.attack ?? null,
                  defense: c.defense ?? null,
                  stamina: c.stamina ?? null,
                  dash: c.dash ?? null,
                  burst_resistance: c.burst_resistance ?? null,
                  weight: c.weight ?? null,
                  series: c.series ?? null,
                  spin_direction: c.spin_direction ?? null,
                  has_integrated_ratchet: !!c.has_integrated_ratchet,
                  is_cx_infinity: !!c.is_cx_infinity,
                })} data-testid={`comp-row-${c.id}-edit`} className="text-white/70 hover:text-[#00E5FF]">{t.edit}</button>
                <button onClick={() => remove(c.id)} data-testid={`comp-row-${c.id}-delete`} className="text-[#FF0055]">{t.delete}</button>
              </div>
            </div>
            {(typeof c.attack === "number" ||
              typeof c.defense === "number" ||
              typeof c.stamina === "number" ||
              typeof c.dash === "number" ||
              typeof c.burst_resistance === "number") && (
              <div className="flex flex-wrap gap-1" data-testid={`comp-row-${c.id}-stats`}>
                {typeof c.attack === "number" && <MiniStat label="A" value={c.attack} color="#FF0055" />}
                {typeof c.defense === "number" && <MiniStat label="D" value={c.defense} color="#00E5FF" />}
                {typeof c.stamina === "number" && <MiniStat label="S" value={c.stamina} color="#E2FF00" />}
                {typeof c.dash === "number" && <MiniStat label="DA" value={c.dash} color="#9B7AFF" />}
                {typeof c.burst_resistance === "number" && <MiniStat label="B" value={c.burst_resistance} color="#FF9B00" />}
              </div>
            )}
            {c.has_integrated_ratchet && (
              <span
                data-testid={`comp-row-${c.id}-ux-badge`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border border-[#9B7AFF]/40 bg-[#9B7AFF]/10 text-[#9B7AFF] w-max"
              >
                {t.uxInfinityBadge}
              </span>
            )}
            {c.is_cx_infinity && (
              <span
                data-testid={`comp-row-${c.id}-cx-infinity-badge`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border border-[#E2FF00]/40 bg-[#E2FF00]/10 text-[#E2FF00] w-max"
              >
                {t.cxInfinityBadge}
              </span>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-white/40 py-12 font-mono text-sm">{t.none}</div>
        )}
      </div>

      {form && (
        <Modal title={form.id ? t.editComponent : t.newComponent} onClose={() => setForm(null)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t.componentName} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="comp-name-input" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.componentType}</label>
              <select data-testid="comp-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#00E5FF]">
                {COMPONENT_TYPES.map((ct) => (
                  <option key={ct} className="bg-[#0C0C14]" value={ct}>{componentTypeLabel(ct)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] font-mono mb-3">
              // {t.editStats}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatSlider label={t.statAttack} value={form.attack} onChange={(v) => setForm({ ...form, attack: v })} color="#FF0055" testid="comp-stat-attack" />
              <StatSlider label={t.statDefense} value={form.defense} onChange={(v) => setForm({ ...form, defense: v })} color="#00E5FF" testid="comp-stat-defense" />
              <StatSlider label={t.statStamina} value={form.stamina} onChange={(v) => setForm({ ...form, stamina: v })} color="#E2FF00" testid="comp-stat-stamina" />
              <StatSlider label={t.statDash} value={form.dash} onChange={(v) => setForm({ ...form, dash: v })} color="#9B7AFF" testid="comp-stat-dash" />
              <StatSlider label={t.statBurst} value={form.burst_resistance} onChange={(v) => setForm({ ...form, burst_resistance: v })} color="#FF9B00" testid="comp-stat-burst" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.statWeight} (g)</label>
                <input
                  data-testid="comp-stat-weight"
                  type="number"
                  step="0.1"
                  min="0"
                  value={form.weight ?? ""}
                  onChange={(e) => setForm({ ...form, weight: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#00E5FF]"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.seriesLabel}</label>
                <select
                  data-testid="comp-stat-series"
                  value={form.series || ""}
                  onChange={(e) => setForm({ ...form, series: e.target.value || null })}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#00E5FF]"
                >
                  <option value="" className="bg-[#0C0C14]">—</option>
                  <option value="BX" className="bg-[#0C0C14]">BX</option>
                  <option value="UX" className="bg-[#0C0C14]">UX</option>
                  <option value="CX" className="bg-[#0C0C14]">CX</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.spinLabel}</label>
                <select
                  data-testid="comp-stat-spin"
                  value={form.spin_direction || ""}
                  onChange={(e) => setForm({ ...form, spin_direction: e.target.value || null })}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#00E5FF]"
                >
                  <option value="" className="bg-[#0C0C14]">—</option>
                  <option value="right" className="bg-[#0C0C14]">{t.spinRight}</option>
                  <option value="left" className="bg-[#0C0C14]">{t.spinLeft}</option>
                  <option value="dual" className="bg-[#0C0C14]">{t.spinDual}</option>
                </select>
              </div>
            </div>
            {form.type === "blade" && (
              <label
                data-testid="comp-integrated-ratchet-row"
                className="flex items-start gap-3 mt-4 p-3 border border-[#9B7AFF]/30 bg-[#9B7AFF]/5 cursor-pointer hover:bg-[#9B7AFF]/10 transition-colors rounded-md"
              >
                <input
                  type="checkbox"
                  data-testid="comp-integrated-ratchet"
                  checked={!!form.has_integrated_ratchet}
                  onChange={(e) => setForm({ ...form, has_integrated_ratchet: e.target.checked })}
                  className="mt-1 accent-[#9B7AFF]"
                />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#9B7AFF] font-mono">
                    {t.uxInfinityLibraryLabel}
                  </div>
                  <p className="text-xs text-white/60 mt-1">{t.uxInfinityLibraryHint}</p>
                </div>
              </label>
            )}
            {(form.type === "main_blade" || form.type === "overblade") && (
              <label
                data-testid="comp-cx-infinity-row"
                className="flex items-start gap-3 mt-4 p-3 border border-[#E2FF00]/30 bg-[#E2FF00]/5 cursor-pointer hover:bg-[#E2FF00]/10 transition-colors rounded-md"
              >
                <input
                  type="checkbox"
                  data-testid="comp-cx-infinity"
                  checked={!!form.is_cx_infinity}
                  onChange={(e) => setForm({ ...form, is_cx_infinity: e.target.checked })}
                  className="mt-1 accent-[#E2FF00]"
                />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E2FF00] font-mono">
                    {t.cxInfinityLibraryLabel}
                  </div>
                  <p className="text-xs text-white/60 mt-1">{t.cxInfinityLibraryHint}</p>
                </div>
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setForm(null)} className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60">{t.cancel}</button>
            <button onClick={save} data-testid="save-component-btn"
              className="px-5 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">{t.save}</button>
          </div>
        </Modal>
      )}

      {bulkOpen && (
        <Modal title={t.bulkAddBtn} onClose={() => setBulkOpen(false)}>
          <p className="text-xs font-mono text-white/50 mb-3">{t.bulkAddHint}</p>
          <textarea
            rows={10}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={t.bulkAddPlaceholder}
            data-testid="bulk-component-textarea"
            className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#00E5FF]"
          />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setBulkOpen(false)} className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60">{t.cancel}</button>
            <button
              onClick={bulkImport}
              disabled={bulkBusy}
              data-testid="bulk-component-submit"
              className="px-5 py-2 bg-[#E2FF00]/15 border border-[#E2FF00]/40 text-[#E2FF00] hover:bg-[#E2FF00]/25 text-xs uppercase tracking-[0.25em] font-bold disabled:opacity-50"
            >
              {bulkBusy ? t.saving : t.bulkAddBtn}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
